import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!loading && user) {
    if (user.role === "institute_staff") {
      // Institute Staff are no longer allowed in either portal.
      return <Navigate to="/institute/login?denied=staff" replace />;
    }
    const dest = user.role === "institute_admin" ? "/institute" : "/admin";
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const me = await login(username.trim(), password);
      if (me?.role === "institute_staff") {
        localStorage.removeItem("gsm_token");
        localStorage.removeItem("gsm_user");
        setError(
          "Access denied. The Institute Staff role no longer has portal access. Please contact your Institute Admin.",
        );
        return;
      }
      const dest = me?.role === "institute_admin" ? "/institute" : "/admin";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#f9f8f6]"
    >
      {/* Left — heritage hero */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          alt="GS Mandal heritage — early campus, 1970s"
          src="https://customer-assets.emergentagent.com/job_trust-admin-hub-1/artifacts/hc6ja172_36.jpg"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1b3326]/85 via-[#234333]/70 to-[#1c1a17]/80" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-sm bg-white flex items-center justify-center p-1 shadow-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_trust-admin-hub-1/artifacts/i2nc9e38_logo.jpeg"
                alt="GS Mandal crest"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="tracking-[0.25em] text-xs uppercase text-white/70">
              Gramaudyogik Shikshan Mandal · Est. 1975
            </div>
          </div>

          <div className="space-y-6">
            <div className="serif-rule w-24 bg-white/40" />
            <h1 className="font-serif text-5xl leading-tight">
              A half-century of learning,<br />
              <span className="italic text-[#d4af37]">now, in one place.</span>
            </h1>
            <p className="max-w-md text-white/75 text-sm leading-relaxed">
              A centralized trust management platform for GS Mandal—overseeing
              eighteen institutions, managing thousands of students, and consolidating
              decades of records into a single, dependable system of record.
            </p>
          </div>

          <div className="flex items-center gap-2 text-white/60 text-xs tracking-wider uppercase">
            <ShieldCheck size={14} strokeWidth={1.5} />
            Private admin console · authorised personnel only
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b] mb-4">
              Trust Administration
            </div>
            <h2 className="font-serif text-4xl text-[#1c1a17] leading-tight">
              Sign in to continue
            </h2>
            <p className="mt-3 text-sm text-[#6b655b]">
              Enter your admin credentials to access the master data console.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
                Username
              </label>
              <input
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-2 w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]"
                autoFocus
                required
              />
            </div>

            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
                Password
              </label>
              <div className="relative mt-2">
                <input
                  data-testid="login-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-4 py-3 pr-11 focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]"
                  required
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b655b] hover:text-[#1c1a17]"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-[#9e2a2b] border border-[#9e2a2b]/30 bg-[#9e2a2b]/5 rounded-sm px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full bg-[#234333] hover:bg-[#1b3326] text-white font-sans text-sm tracking-wide uppercase py-3 rounded-sm transition-colors disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>

            <div className="text-[11px] text-[#6b655b] text-center pt-4">
              Issues accessing your account? Contact the Trust IT Office.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
